import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { label, timestamp } from "./format";
import {
  describeReason,
  describeWorkerReason,
  environmentLabel,
  type RenderQueueRow,
  type RenderQueueView,
  useRenderQueue,
  waitLabel,
} from "./renderQueue";

// "Ready to render": Echoes whose composer execution is waiting at the render
// admission gate for an owned GPU (plan 2026-09-26 H3Q-09). Queue wait is
// shown apart from H3 render time; positions are approximate and a waiter
// that stopped polling is labelled stale instead of keeping its place.

export function ReadyToRender({
  refreshSeconds,
  onSelect,
}: {
  refreshSeconds: number;
  onSelect: (workload: string) => void;
}) {
  const queue = useRenderQueue(refreshSeconds);
  return (
    <section aria-label="Ready to render" className="space-y-3">
      <div>
        <h2 className="font-semibold">Ready to render</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Echoes whose film is ready to be rendered, held at the render gate
          until an owned GPU is free or waiting would miss the delivery promise.
          Queue wait here is separate from render time.
        </p>
      </div>
      {queue.unavailable ? (
        <p
          role="status"
          className="rounded-lg border p-4 text-sm text-muted-foreground"
        >
          Render queue unavailable: {queue.unavailable} Echoes still render
          without waiting when the gate is not deployed.
        </p>
      ) : null}
      {queue.error ? (
        <p
          role="status"
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm"
        >
          {queue.data
            ? "The render queue could not be refreshed. The rows below are from the last successful read."
            : "The render queue could not be read. Refresh to try again."}
        </p>
      ) : null}
      {!queue.data && queue.loading && !queue.unavailable ? (
        <p role="status" className="text-sm">
          Loading render queue…
        </p>
      ) : null}
      {queue.data ? (
        <RenderQueueBody
          view={queue.data}
          current={!queue.error}
          onSelect={onSelect}
        />
      ) : null}
    </section>
  );
}

function RenderQueueBody({
  view,
  current,
  onSelect,
}: {
  view: RenderQueueView;
  current: boolean;
  onSelect: (workload: string) => void;
}) {
  const { summary } = view;
  return (
    <>
      <p className="text-xs text-muted-foreground">
        {environmentLabel(view.environment)} · Read {timestamp(view.as_of)}
        {current ? "" : " (not current)"} · {summary.waiting} waiting ·{" "}
        {summary.admitted} rendering · longest wait{" "}
        {waitLabel(summary.oldest_wait_seconds)}
        {summary.deadline_at_risk
          ? ` · ${summary.deadline_at_risk} at risk of missing the promise`
          : ""}
        {summary.stale_waiters ? ` · ${summary.stale_waiters} stale` : ""}
      </p>
      {view.waiting.length ? (
        <div className="divide-y rounded-lg border">
          {view.waiting.map((row) => (
            <WaitingRow key={row.generation} row={row} onSelect={onSelect} />
          ))}
        </div>
      ) : (
        <p className="rounded-lg border p-4 text-sm text-muted-foreground">
          No Echoes are waiting for a GPU slot.
        </p>
      )}
      {view.needs_operator.length ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">
            Needs attention ({view.needs_operator.length})
          </h3>
          <div className="divide-y rounded-lg border border-destructive/40">
            {view.needs_operator.map((row) => (
              <div
                key={row.generation}
                className="flex flex-wrap items-center gap-2 p-3 text-sm"
              >
                <WorkloadButton
                  workload={row.workload_id}
                  onSelect={onSelect}
                />
                <span>{describeReason(row.attention_reason)}</span>
                <span className="text-muted-foreground">
                  · ready {timestamp(row.ready_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {view.admitted.length || view.recent.length ? (
        <details className="rounded-lg border p-3 text-sm">
          <summary className="cursor-pointer">
            Rendering ({view.admitted.length}) and recently released (
            {view.recent.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {[...view.admitted, ...view.recent].map((row) => (
              <li
                key={row.generation}
                className="flex flex-wrap items-center gap-2"
              >
                <WorkloadButton
                  workload={row.workload_id}
                  onSelect={onSelect}
                />
                <Badge variant="outline">
                  {row.state === "ADMITTED"
                    ? "rendering"
                    : label(row.outcome ?? row.state.toLowerCase())}
                </Badge>
                <span>
                  waited {waitLabel(row.queue_wait_seconds)} ·{" "}
                  {describeReason(row.admission_reason)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}

function WaitingRow({
  row,
  onSelect,
}: {
  row: RenderQueueRow;
  onSelect: (workload: string) => void;
}) {
  const forecast = row.forecast;
  const blocked = Object.entries(row.not_schedulable ?? {});
  return (
    <div
      className="grid gap-2 p-4 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
      data-render-queue-row={row.workload_id}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <WorkloadButton workload={row.workload_id} onSelect={onSelect} />
          {row.stale ? (
            <Badge variant="destructive">stale · stopped polling</Badge>
          ) : row.approximate_position != null ? (
            <Badge variant="secondary">
              ~#{row.approximate_position + 1} in line
            </Badge>
          ) : null}
          {forecast?.deadline_at_risk ? (
            <Badge variant="destructive">promise at risk</Badge>
          ) : null}
          {row.mode === "shadow" ? (
            <Badge variant="outline">shadow</Badge>
          ) : null}
          {row.cancel_requested ? (
            <Badge variant="outline">cancel requested</Badge>
          ) : null}
        </div>
        <p className="text-sm">
          Waiting {waitLabel(row.queue_wait_seconds)} ·{" "}
          {describeReason(row.decision_reason)}
        </p>
        <p className="text-xs text-muted-foreground">
          {[row.product_flow, row.tier]
            .filter(Boolean)
            .map((v) => label(String(v)))
            .join(" · ")}
          {row.h3_clips != null ? ` · ${row.h3_clips} clips` : ""}
          {row.stale
            ? ` · last poll ${timestamp(row.last_poll_at)}`
            : row.ahead
              ? ` · ${row.ahead} ahead`
              : ""}
          {row.shadow_reason
            ? ` · enforce would: ${describeReason(row.shadow_reason)}`
            : ""}
        </p>
      </div>
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          Promise:{" "}
          {row.promise_deadline_at
            ? `${timestamp(row.promise_deadline_at)} (${label(row.promise_basis)})`
            : "none"}
        </p>
        {forecast ? (
          <p>
            Finish if it waits: {timestamp(forecast.finish_if_wait_at)}
            {forecast.slack_seconds != null
              ? ` · slack ${forecast.slack_seconds < 0 ? "−" : ""}${waitLabel(Math.abs(forecast.slack_seconds))}`
              : ""}
          </p>
        ) : null}
        {blocked.length ? (
          <p>
            Owned GPUs not counted:{" "}
            {blocked
              .map(
                ([worker, reasons]) =>
                  `${worker} (${reasons.map(describeWorkerReason).join(", ")})`,
              )
              .join("; ")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function WorkloadButton({
  workload,
  onSelect,
}: {
  workload: string;
  onSelect: (workload: string) => void;
}) {
  return (
    <Button
      variant="link"
      className="h-auto p-0 font-mono text-sm"
      data-queue-workload={workload}
      onClick={() => onSelect(workload)}
    >
      {workload}
    </Button>
  );
}
